// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
'use client'

import React, { useCallback, useState } from 'react'

import { Mention, MentionsInput } from 'react-mentions'

import { useSearchMentionUsers } from '@/hooks/queries/users'
import { cn } from '@/lib/cn'
import { sanitizeHTML } from '@/utils/sanitize'

export type MentionUser = {
  id: string
  display: string
}

export type MentionTextareaProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  users?: MentionUser[]
  isError?: boolean
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'value' | 'onChange'>

const MentionTextarea = React.forwardRef<HTMLDivElement, MentionTextareaProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Digite @ para mencionar alguém...',
      className,
      disabled = false,
      users: propUsers,
      ...props
    },
    ref,
  ) => {
    const [searchQuery, setSearchQuery] = useState('')
    const { users: searchedUsers } = useSearchMentionUsers(searchQuery)

    const getMentionData = useCallback(
      (query: string) => {
        if (propUsers) {
          return propUsers
            .filter((user) =>
              user.display.toLowerCase().includes(query.toLowerCase()),
            )
            .map((user) => ({
              id: user.id,
              display: user.display,
            }))
        }

        setSearchQuery(query)
        return searchedUsers.map((user) => ({
          id: user.id,
          display: user.name,
        }))
      },
      [propUsers, searchedUsers],
    )

    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        <MentionsInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            control: {
              backgroundColor: '#fff',
              fontSize: '14px',
              fontWeight: 'normal',
              border: '1px solid hsl(var(--system-border-primary))',
              borderRadius: '0.5rem',
              padding: '8px 12px',
              minHeight: '112px',
              maxHeight: '500px',
            },
            '&multiLine': {
              control: {
                fontFamily: 'inherit',
                minHeight: '112px',
              },
              highlighter: {
                border: '1px solid transparent',
                minHeight: '112px',
              },
              input: {
                padding: '8px 12px',
                border: '1px solid transparent',
                outline: 'none',
                minHeight: '112px',
              },
            },
            suggestions: {
              list: {
                backgroundColor: '#fff',
                border: '1px solid hsl(var(--system-border-primary))',
                borderRadius: '0.5rem',
                fontSize: '14px',
                maxHeight: '200px',
                overflowY: 'auto',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              },
              item: {
                padding: '8px 12px',
                '&focused': {
                  backgroundColor: 'hsl(var(--system-selected-blue))',
                  color: 'hsl(var(--system-text-primary))',
                },
              },
            },
          }}
        >
          <Mention
            trigger="@"
            data={getMentionData}
            displayTransform={(_id, display) => `@${display}`}
            renderSuggestion={(suggestion) => (
              <div className="flex items-center">
                <span>{suggestion.display}</span>
              </div>
            )}
            style={{
              backgroundColor: '#e8d5f2',
              borderRadius: '4px',
              fontWeight: 500,
            }}
          />
        </MentionsInput>
      </div>
    )
  },
)

MentionTextarea.displayName = 'MentionTextarea'

export { MentionTextarea }

export type MentionViewProps = {
  content: string
  classMention?: string
} & React.ComponentPropsWithoutRef<'span'>

const MentionView = React.forwardRef<HTMLSpanElement, MentionViewProps>(
  ({ content, className, classMention, ...props }, ref) => {
    const processMentions = useCallback((text: string) => {
      const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g
      const parts: Array<{
        type: 'text' | 'mention'
        content: string
        id?: string
      }> = []
      let lastIndex = 0
      let match: RegExpExecArray | null

      // biome-ignore lint/suspicious/noAssignInExpressions: atribuição intencional em expressão (loop de regex.exec)
      while ((match = mentionRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push({
            type: 'text',
            content: text.slice(lastIndex, match.index),
          })
        }

        parts.push({
          type: 'mention',
          content: match[1],
          id: match[2],
        })

        lastIndex = match.index + match[0].length
      }

      if (lastIndex < text.length) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex),
        })
      }

      return parts.length > 0 ? parts : [{ type: 'text', content: text }]
    }, [])

    const parts = processMentions(content)

    return (
      <span
        ref={ref}
        className={cn('whitespace-pre-wrap break-words', className)}
        {...props}
      >
        {parts.map((part, index) => {
          if (part.type === 'mention') {
            return (
              <span
                key={index}
                className={cn(
                  'rounded-md bg-[#e8d5f2] text-sm font-normal',
                  classMention,
                )}
              >
                @{part.content}
              </span>
            )
          }
          return (
            <span
              key={index}
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(part.content),
              }}
            />
          )
        })}
      </span>
    )
  },
)

MentionView.displayName = 'MentionView'

export { MentionView }
